import { zodResolver } from '@hookform/resolvers/zod';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Alert, Box, Button, IconButton, InputAdornment, Paper, Stack, TextField, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { z } from 'zod';
import { ApiError } from '@/api/errors';
import { LanguageSwitcher } from '@/app/layout/LanguageSwitcher';
import { authApi } from './authApi';
import { lastTenant, useAuthStore } from './authStore';

const schema = z.object({
  tenant: z.string().trim().min(1).max(64),
  username: z.string().trim().min(1).max(100),
  password: z.string().min(1).max(128),
});
type FormValues = z.infer<typeof schema>;

/** Only allow in-app relative redirects (no open redirect). */
function safeReturnTo(value: string | null): string {
  return value && value.startsWith('/') && !value.startsWith('//') ? value : '/customers';
}

/** Tenant prefill order: ?tenant=, subdomain (acme.aml.example.com), last used. */
function initialTenant(params: URLSearchParams): string {
  const fromQuery = params.get('tenant');
  if (fromQuery) return fromQuery;
  const parts = window.location.hostname.split('.');
  if (parts.length > 2 && parts[0] !== 'www') return parts[0] ?? '';
  return lastTenant.get();
}

export function LoginPage() {
  const { t } = useTranslation(['auth', 'errors', 'common']);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const status = useAuthStore((s) => s.status);
  const setSession = useAuthStore((s) => s.setSession);
  const [showPassword, setShowPassword] = useState(false);

  const { register, handleSubmit, formState } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { tenant: initialTenant(params), username: '', password: '' },
  });

  const login = useMutation({
    mutationFn: authApi.login,
    onSuccess: (data, variables) => {
      lastTenant.set(variables.tenant);
      setSession(data);
      navigate(safeReturnTo(params.get('returnTo')), { replace: true });
    },
    meta: { silent: true },
  });

  if (status === 'authenticated') return <Navigate to={safeReturnTo(params.get('returnTo'))} replace />;

  const error = login.error instanceof ApiError ? login.error : null;
  const required = t('auth:required');

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: 'background.default', p: 2 }}>
      <Box sx={{ position: 'fixed', top: 12, right: 12 }}>
        <LanguageSwitcher />
      </Box>
      <Paper variant="outlined" sx={{ width: '100%', maxWidth: 420, p: { xs: 3, sm: 5 } }}>
        <Stack spacing={1} sx={{ mb: 4, alignItems: 'center', textAlign: 'center' }}>
          <Box component="img" src="/favicon.svg" alt="" sx={{ width: 44, height: 44 }} />
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
            {t('auth:title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('auth:subtitle')}
          </Typography>
        </Stack>

        <Stack component="form" spacing={2.5} noValidate onSubmit={handleSubmit((values) => login.mutate(values))}>
          {params.get('expired') && !error ? <Alert severity="info">{t('auth:sessionExpired')}</Alert> : null}
          {error ? <Alert severity="error">{t(`errors:${error.code}`, { defaultValue: t('errors:UNKNOWN') })}</Alert> : null}

          <TextField
            label={t('auth:tenant')}
            helperText={formState.errors.tenant ? required : t('auth:tenantHelp')}
            error={!!formState.errors.tenant}
            autoComplete="organization"
            size="medium"
            {...register('tenant')}
          />
          <TextField
            label={t('auth:username')}
            error={!!formState.errors.username}
            helperText={formState.errors.username ? required : undefined}
            autoComplete="username"
            autoFocus
            size="medium"
            {...register('username')}
          />
          <TextField
            label={t('auth:password')}
            type={showPassword ? 'text' : 'password'}
            error={!!formState.errors.password}
            helperText={formState.errors.password ? required : undefined}
            autoComplete="current-password"
            size="medium"
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((s) => !s)}
                      edge="end"
                      aria-label={t(showPassword ? 'auth:hidePassword' : 'auth:showPassword')}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
            {...register('password')}
          />
          <Button type="submit" variant="contained" size="large" loading={login.isPending}>
            {t('auth:submit')}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
